import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Sparkles, ArrowRight, Loader2, ChevronRight, Search
} from "lucide-react";
import { API, LOGO_URL } from "@/config";

export default function SolutionsDirectoryPage() {
  const [niches, setNiches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/niches`),
      axios.get(`${API}/niches/categories`),
    ]).then(([nichesRes, catsRes]) => {
      setNiches(nichesRes.data.niches);
      setCategories(catsRes.data.categories);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = niches.filter(n => {
    const matchesCat = !activeCategory || n.category === activeCategory;
    const matchesSearch = !search || n.name.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <>
      <Helmet>
        <title>AI Content Solutions for Every Profession — Creatara</title>
        <meta name="description" content="Find AI-powered content creation tools built for your profession. From real estate agents to SaaS founders, fitness coaches to lawyers — generate monetization-ready content in seconds." />
        <meta property="og:title" content="AI Content Solutions for Every Profession — Creatara" />
        <meta property="og:description" content="AI content creation tools for 100+ professions. Generate niche-specific, monetization-ready content." />
        <meta name="keywords" content="AI content creator, content monetization, niche content generator, Creatara, content for professionals" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara - AI Content Solutions Directory" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold">Creatara</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/templates" className="text-muted-foreground hover:text-foreground text-sm hidden sm:block">Templates</Link>
              <Link to="/" className="text-muted-foreground hover:text-foreground text-sm hidden sm:block">Home</Link>
              <Button onClick={() => navigate("/register")} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-6">
                Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="hero-glow pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Badge className="mb-6 bg-violet-600/10 text-violet-500 border-violet-600/20 px-4 py-1">
                {niches.length}+ Niche Solutions
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight gradient-text mb-4">
                AI Content Built for<br />Your Profession
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Find your niche below. Each solution comes with a sample content calendar and AI templates tailored to your industry.
              </p>
              <div className="max-w-md mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search your profession..."
                  className="h-14 pl-12 text-lg bg-white/5 border-white/20 rounded-full"
                  data-testid="niche-search"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="px-6 pb-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                variant={!activeCategory ? "default" : "ghost"}
                size="sm"
                className={`rounded-full ${!activeCategory ? "bg-violet-600" : ""}`}
                onClick={() => setActiveCategory(null)}
              >
                All ({niches.length})
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.key}
                  variant={activeCategory === cat.key ? "default" : "ghost"}
                  size="sm"
                  className={`rounded-full ${activeCategory === cat.key ? "bg-violet-600" : ""}`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label} ({cat.count})
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Niche Grid */}
        <section className="py-8 px-6">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-16">No niches found. Try a different search.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filtered.map((n, i) => (
                  <motion.div key={n.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.5) }}>
                    <Link to={`/solutions/${n.slug}`}>
                      <Card className="glass card-hover h-full group" data-testid={`niche-card-${n.slug}`}>
                        <CardContent className="p-4">
                          <p className="font-semibold text-sm group-hover:text-violet-400 transition-colors">{n.name}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.description}</p>
                          <div className="flex items-center gap-1 mt-2 text-violet-500 text-xs font-medium">
                            View solution <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-card/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Don't See Your Niche?</h2>
            <p className="text-muted-foreground mb-8">Creatara works for any profession. Sign up free and generate content for any topic.</p>
            <Button onClick={() => navigate("/register")} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 py-6 text-lg">
              Start Free — Works for Any Niche <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><img src={LOGO_URL} alt="Creatara - Content Monetization for Every Profession" className="w-8 h-8 rounded-lg" /><span className="font-semibold">Creatara</span></div>
            <p className="text-sm text-muted-foreground">© 2026 ProJohnson Consulting Ltd. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
